import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Clock, CheckCircle, MapPin, Users, Phone, Mail, ArrowRight, MessageSquare, ShieldCheck, Zap } from 'lucide-react';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    service: '',
    buildingType: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    alert('Enquiry submitted successfully.');
  };
  const inputClass = 'w-full bg-surface/50 rounded-xl py-4 px-5 text-base text-primary-dark placeholder:text-primary-dark/40 focus:outline-none focus:bg-white focus:ring-2 focus:ring-accent/50 transition-all border border-transparent focus:border-accent/20';
  return <main className="min-h-screen font-sans bg-white" data-id="element-945">
      {/* Hero Section */}
      <section className="bg-primary-dark pt-36 pb-24 relative overflow-hidden" data-id="element-946">
        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-accent/5 rounded-bl-full blur-3xl transform translate-x-1/3 -translate-y-1/4 pointer-events-none" data-id="element-947"></div>

        <div className="container mx-auto px-6 relative z-10" data-id="element-948">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-949">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-950">
              <span className="text-accent font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-951">
                Get in Touch
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-952">
                Talk to our commercial HVAC team
              </h1>
              <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-xl" data-id="element-953">
                We work exclusively with commercial properties. Tell us about
                your building and we will come back to you within 4 hours to
                discuss your requirements.
              </p>

              <div className="flex flex-wrap gap-4" data-id="element-954">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-white border border-white/10" data-id="element-955">
                  <Clock className="w-4 h-4 text-accent" data-id="element-956" /> 4-hour response
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-white border border-white/10" data-id="element-957">
                  <MapPin className="w-4 h-4 text-accent" data-id="element-958" /> Nationwide coverage
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-white border border-white/10" data-id="element-959">
                  <Users className="w-4 h-4 text-accent" data-id="element-960" /> In-house engineers
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="hidden lg:block relative" data-id="element-961">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl relative bg-gray-300 flex items-center justify-center" data-id="element-962">
                <div className="text-center" data-id="element-963">
                  <div className="w-16 h-16 bg-gray-400/50 rounded-full mx-auto mb-3 flex items-center justify-center" data-id="element-964">
                    <Users className="w-8 h-8 text-gray-500" data-id="element-965" />
                  </div>
                  <p className="text-gray-500 font-medium text-sm" data-id="element-966">
                    Image placeholder
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Strip */}
      <section className="py-12 border-b border-gray-100 bg-white relative z-20" data-id="element-967">
        <div className="container mx-auto px-6" data-id="element-968">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-969">
            {[{
            icon: Zap,
            title: 'Fast response',
            desc: 'Guaranteed 4-hour response time for all commercial enquiries.'
          }, {
            icon: Users,
            title: 'Expert engineers',
            desc: 'Directly employed, fully qualified in-house technical team.'
          }, {
            icon: ShieldCheck,
            title: 'No obligation',
            desc: 'Free initial consultation and comprehensive site surveys.'
          }, {
            icon: MapPin,
            title: 'Nationwide',
            desc: 'Supporting commercial clients across the entire UK.'
          }].map((value, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="flex items-start gap-4" data-id="element-970">
                <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-971">
                  <value.icon className="w-6 h-6 text-accent" data-id="element-972" />
                </div>
                <div data-id="element-973">
                  <h3 className="font-bold text-primary-dark mb-1" data-id="element-974">
                    {value.title}
                  </h3>
                  <p className="text-sm text-gray-600" data-id="element-975">{value.desc}</p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Form & Info Section */}
      <section className="bg-primary-light py-24" data-id="element-976">
        <div className="container mx-auto px-6" data-id="element-977">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden" data-id="element-978">
            <div className="grid lg:grid-cols-5" data-id="element-979">
              {/* Left Column: Form */}
              <div className="lg:col-span-3 p-8 md:p-12 lg:p-16" data-id="element-980">
                <div className="mb-10" data-id="element-981">
                  <h2 className="text-3xl font-bold text-primary-dark mb-4" data-id="element-982">
                    Send us a message
                  </h2>
                  <p className="text-gray-600" data-id="element-983">
                    Fill out the form below and our commercial team will get
                    back to you promptly.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-984">
                  <div className="grid md:grid-cols-2 gap-6" data-id="element-985">
                    <div data-id="element-986">
                      <label htmlFor="name" className="block text-sm font-bold text-primary-dark mb-2.5" data-id="element-987">
                        Full name *
                      </label>
                      <input required type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={inputClass} placeholder="Your name" data-id="element-988" />
                    </div>
                    <div data-id="element-989">
                      <label htmlFor="company" className="block text-sm font-bold text-primary-dark mb-2.5" data-id="element-990">
                        Company name *
                      </label>
                      <input required type="text" id="company" name="company" value={formData.company} onChange={handleChange} className={inputClass} placeholder="Business name" data-id="element-991" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6" data-id="element-992">
                    <div data-id="element-993">
                      <label htmlFor="email" className="block text-sm font-bold text-primary-dark mb-2.5" data-id="element-994">
                        Work email *
                      </label>
                      <input required type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} placeholder="you@company.co.uk" data-id="element-995" />
                    </div>
                    <div data-id="element-996">
                      <label htmlFor="phone" className="block text-sm font-bold text-primary-dark mb-2.5" data-id="element-997">
                        Phone number *
                      </label>
                      <input required type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} placeholder="07xxx xxxxxx" data-id="element-998" />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6" data-id="element-999">
                    <div data-id="element-1000">
                      <label htmlFor="service" className="block text-sm font-bold text-primary-dark mb-2.5" data-id="element-1001">
                        Service needed
                      </label>
                      <select id="service" name="service" value={formData.service} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-1002">
                        <option value="" data-id="element-1003">Select a service...</option>
                        <option value="Installation" data-id="element-1004">Installation</option>
                        <option value="Maintenance" data-id="element-1005">Maintenance</option>
                        <option value="TM44 compliance" data-id="element-1006">TM44 compliance</option>
                        <option value="Ventilation" data-id="element-1007">Ventilation</option>
                        <option value="FM support" data-id="element-1008">FM support</option>
                        <option value="Not sure" data-id="element-1009">Not sure</option>
                      </select>
                    </div>
                    <div data-id="element-1010">
                      <label htmlFor="buildingType" className="block text-sm font-bold text-primary-dark mb-2.5" data-id="element-1011">
                        Building type
                      </label>
                      <select id="buildingType" name="buildingType" value={formData.buildingType} onChange={handleChange} className={`${inputClass} appearance-none`} data-id="element-1012">
                        <option value="" data-id="element-1013">Select building type...</option>
                        <option value="Office" data-id="element-1014">Office</option>
                        <option value="Retail" data-id="element-1015">Retail</option>
                        <option value="Hospitality" data-id="element-1016">Hospitality</option>
                        <option value="Healthcare" data-id="element-1017">Healthcare</option>
                        <option value="Education" data-id="element-1018">Education</option>
                        <option value="Industrial" data-id="element-1019">Industrial</option>
                        <option value="Other" data-id="element-1020">Other</option>
                      </select>
                    </div>
                  </div>

                  <div data-id="element-1021">
                    <label htmlFor="message" className="block text-sm font-bold text-primary-dark mb-2.5" data-id="element-1022">
                      Message
                    </label>
                    <textarea id="message" name="message" rows={4} value={formData.message} onChange={handleChange} className={`${inputClass} resize-none`} placeholder="Tell us about your requirement..." data-id="element-1023"></textarea>
                  </div>

                  <button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white font-bold py-4 rounded-full transition-colors mt-4 flex items-center justify-center gap-2" data-id="element-1024">
                    Send enquiry <ArrowRight className="w-5 h-5" data-id="element-1025" />
                  </button>
                </form>
              </div>

              {/* Right Column: Info */}
              <div className="lg:col-span-2 bg-primary-dark p-8 md:p-12 lg:p-16 text-white relative overflow-hidden" data-id="element-1026">
                {/* Decorative background pattern */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2 pointer-events-none" data-id="element-1027"></div>

                <div className="relative z-10 h-full flex flex-col" data-id="element-1028">
                  <h3 className="text-2xl font-bold mb-8" data-id="element-1029">
                    Contact information
                  </h3>

                  <div className="space-y-8 mb-12 flex-grow" data-id="element-1030">
                    <div className="flex items-start gap-4 group" data-id="element-1031">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent transition-colors" data-id="element-1032">
                        <Phone className="w-5 h-5 text-accent group-hover:text-white transition-colors" data-id="element-1033" />
                      </div>
                      <div data-id="element-1034">
                        <p className="text-sm text-white/60 mb-1" data-id="element-1035">
                          Call us directly
                        </p>
                        <a href="tel:02476998234" className="text-xl font-bold hover:text-accent transition-colors" data-id="element-1036">
                          024 7699 8234
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 group" data-id="element-1037">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent transition-colors" data-id="element-1038">
                        <Mail className="w-5 h-5 text-accent group-hover:text-white transition-colors" data-id="element-1039" />
                      </div>
                      <div data-id="element-1040">
                        <p className="text-sm text-white/60 mb-1" data-id="element-1041">
                          Email our team
                        </p>
                        <a href="mailto:info@accutemp.co.uk" className="text-lg font-bold hover:text-accent transition-colors" data-id="element-1042">
                          info@accutemp.co.uk
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-4 group" data-id="element-1043">
                      <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-accent transition-colors" data-id="element-1044">
                        <MapPin className="w-5 h-5 text-accent group-hover:text-white transition-colors" data-id="element-1045" />
                      </div>
                      <div data-id="element-1046">
                        <p className="text-sm text-white/60 mb-1" data-id="element-1047">
                          Headquarters
                        </p>
                        <p className="text-lg font-medium leading-relaxed" data-id="element-1048">
                          Justa House,
                          <br data-id="element-1049" />
                          204-206 Holbrook Lane,
                          <br data-id="element-1050" />
                          Coventry, CV6 4DD
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/10" data-id="element-1051">
                    <div className="flex items-center gap-3 mb-2" data-id="element-1052">
                      <ShieldCheck className="w-5 h-5 text-accent" data-id="element-1053" />
                      <h4 className="font-bold" data-id="element-1054">Commercial only</h4>
                    </div>
                    <p className="text-sm text-white/70 leading-relaxed" data-id="element-1055">
                      We specialise exclusively in commercial properties. For
                      residential enquiries, please contact a domestic
                      installer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-24 bg-white" data-id="element-1056">
        <div className="container mx-auto px-6" data-id="element-1057">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1058">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="order-2 lg:order-1" data-id="element-1059">
              <div className="aspect-video lg:aspect-square rounded-3xl overflow-hidden shadow-lg relative bg-surface flex items-center justify-center" data-id="element-1060">
                <div className="text-center px-8" data-id="element-1061">
                  <MapPin className="w-12 h-12 text-primary-dark/20 mx-auto mb-4" data-id="element-1062" />
                  <p className="text-primary-dark/40 font-medium" data-id="element-1063">
                    Map or office image placeholder
                  </p>
                  <p className="text-primary-dark/30 text-sm mt-1" data-id="element-1064">
                    Coventry, CV6 4DD
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="order-1 lg:order-2 lg:pl-12" data-id="element-1065">
              <span className="text-accent font-semibold tracking-wider uppercase text-sm mb-4 block" data-id="element-1066">
                Visit us
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-primary-dark mb-6" data-id="element-1067">
                Our headquarters
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed" data-id="element-1068">
                Based in Coventry, we are perfectly positioned to support
                commercial clients across the Midlands and nationwide. Our
                central location allows for rapid deployment of our engineering
                teams.
              </p>

              <div className="bg-surface/50 p-6 rounded-2xl mb-8" data-id="element-1069">
                <p className="font-medium text-primary-dark text-lg mb-2" data-id="element-1070">
                  Address
                </p>
                <p className="text-gray-600" data-id="element-1071">
                  Justa House, 204-206 Holbrook Lane,
                  <br data-id="element-1072" />
                  Coventry, CV6 4DD
                </p>
              </div>

              <a href="https://maps.google.com/?q=Justa+House+204-206+Holbrook+Lane+Coventry+CV6+4DD" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-primary-dark hover:bg-primary-dark/90 text-white px-8 py-4 rounded-full font-bold transition-colors shadow-sm" data-id="element-1073">
                <MapPin className="w-5 h-5" data-id="element-1074" />
                Get directions on Google Maps
              </a>
            </motion.div>
          </div>
        </div>
      </section>
    </main>;
}